import React, { useState, useEffect, useMemo, useCallback, useRef } from "react"
import { Table, Col, Row, Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from "reactstrap"
import { AvForm, AvField, AvRadioGroup, AvRadio } from "availity-reactstrap-validation"
import api from "API/Api"
import SweetAlert from "react-bootstrap-sweetalert"
import loader from "./../../assets/loading/loader.gif"
import { usePermissions } from "API/PermissionContext"

const VlccBonusBills = () => {
  const { canAdd, canEdit, canDelete, canExport, MODULE } = usePermissions()
  const MID = MODULE.BONUS_BILLS || 23
  const R = "\u20B9"
  const today = new Date().toISOString().split("T")[0]

  // List state
  const [data, setData]           = useState([])
  const [dataLoaded, setDataLoaded] = useState(false)
  const [search, setSearch]       = useState("")
  const [checkedItems, setCheckedItems] = useState({})
  const [parentChecked, setParentChecked] = useState(false)

  // Alerts
  const [deleteId, setDeleteId]   = useState("")
  const [confirm_alert, setConfirmAlert]   = useState(false)
  const [confirm_multi, setConfirmMulti]   = useState(false)
  const [success_dlg, setSuccessDlg]       = useState(false)
  const [dlg_title, setDlgTitle]           = useState("")
  const [dlg_desc, setDlgDesc]             = useState("")

  // Column filters
  const [showFilter, setShowFilter] = useState(false)
  const [fPlant, setFPlant]         = useState("")
  const [fCC, setFCC]               = useState("")
  const [fRoute, setFRoute]         = useState("")
  const [fCenter, setFCenter]       = useState("")
  const [fDateFrom, setFDateFrom]   = useState("")
  const [fDateTo, setFDateTo]       = useState("")
  const [fType, setFType]           = useState("")
  const clearFilters = () => { setFPlant(""); setFCC(""); setFRoute(""); setFCenter(""); setFDateFrom(""); setFDateTo(""); setFType("") }

  // Modals
  const [addModal, setAddModal]   = useState(false)
  const [editModal, setEditModal] = useState(false)
  const [editData, setEditData]   = useState(null)
  const [saving, setSaving]       = useState(false)
  const addFormRef  = useRef(null)
  const editFormRef = useRef(null)

  // Master data
  const [companies, setCompanies] = useState([])
  const [allPlants, setAllPlants] = useState([])

  // Add form cascade
  const [aPlants, setAPlants]   = useState([])
  const [aCCs, setACCs]         = useState([])
  const [aRoutes, setARoutes]   = useState([])
  const [aColls, setAColls]     = useState([])

  // Edit form cascade
  const [ePlants, setEPlants]   = useState([])
  const [eCCs, setECCs]         = useState([])
  const [eRoutes, setERoutes]   = useState([])
  const [eColls, setEColls]     = useState([])

  // Dynamic label for Bonus Value based on Calculate On
  const [aCalcOn, setACalcOn] = useState("1")  // for add modal
  const [eCalcOn, setECalcOn] = useState("1")  // for edit modal
  const bonusValueLabel = calcOn => calcOn === "2" ? "Bonus per KG *" : calcOn === "3" ? "Bonus % *" : "Bonus per Liter *"

  const arr = r => Array.isArray(r.data?.data) ? r.data.data : r.data?.data?.data || []

  // Load master data once
  useEffect(() => {
    api.get("companies").then(r => setCompanies(arr(r))).catch(() => {})
    api.get("plants").then(r => { setAllPlants(arr(r)); setAPlants(arr(r)); setEPlants(arr(r)) }).catch(() => {})
    fetchData()
  }, [])

  const fetchData = useCallback(async () => {
    setDataLoaded(false); setCheckedItems({}); setParentChecked(false)
    try {
      const r = await api.get("center-bonus-bills?per_page=500")
      setData(Array.isArray(r.data?.data) ? r.data.data : r.data?.data?.data || [])
    } catch (e) { setData([]) }
    setDataLoaded(true)
  }, [])

  const showCompany = companies.length > 1

  // Add form cascade handlers
  const aCompanyChange = async v => {
    setACCs([]); setARoutes([]); setAColls([])
    if (v) { try { setAPlants(arr(await api.get("plants-by-company/" + v))) } catch (e) {} }
    else setAPlants(allPlants)
  }
  const aPlantChange = async v => {
    setACCs([]); setARoutes([]); setAColls([])
    if (v) { try { setACCs(arr(await api.get("chillings-by-plant/" + v))) } catch (e) {} }
  }
  const aCCChange = async v => {
    setARoutes([]); setAColls([])
    if (v) { try { setARoutes(arr(await api.get("route-cc/" + v))) } catch (e) {} }
  }
  const aRouteChange = async v => {
    setAColls([])
    if (v) { try { setAColls(arr(await api.get("collection-center-route/" + v))) } catch (e) {} }
  }

  // Edit form cascade handlers
  const ePlantChange = async v => {
    setECCs([]); setERoutes([]); setEColls([])
    if (v) { try { setECCs(arr(await api.get("chillings-by-plant/" + v))) } catch (e) {} }
  }
  const eCCChange = async v => {
    setERoutes([]); setEColls([])
    if (v) { try { setERoutes(arr(await api.get("route-cc/" + v))) } catch (e) {} }
  }
  const eRouteChange = async v => {
    setEColls([])
    if (v) { try { setEColls(arr(await api.get("collection-center-route/" + v))) } catch (e) {} }
  }

  const handleAdd = async (e, v) => {
    if (v.bonusFrom && v.bonusTo && v.bonusFrom > v.bonusTo) return alert("Bonus From must be before Bonus To.")
    setSaving(true)
    try {
      const r = await api.post("create-center-bonus-bill", v)
      if (r.data.success) { fetchData(); setAddModal(false) }
      else alert(r.data.message || JSON.stringify(r.data.errors || r.data.data))
    } catch (e) { alert("Error: " + (e.response?.data?.data || e.message)) }
    setSaving(false)
  }

  const openEdit = async id => {
    try {
      const r = await api.get("center-bonus-bill/" + id)
      const d = r.data.data
      setEditData(d)
      setECalcOn(d?.calculateOn || "1")
      setEPlants(allPlants); setECCs([]); setERoutes([]); setEColls([])
      if (d?.plant) { try { setECCs(arr(await api.get("chillings-by-plant/" + d.plant))) } catch (e) {} }
      if (d?.chillingCenter) { try { setERoutes(arr(await api.get("route-cc/" + d.chillingCenter))) } catch (e) {} }
      if (d?.route) { try { setEColls(arr(await api.get("collection-center-route/" + d.route))) } catch (e) {} }
      setEditModal(true)
    } catch (e) { alert("Could not load record.") }
  }

  const handleEdit = async (e, v) => {
    if (v.bonusFrom && v.bonusTo && v.bonusFrom > v.bonusTo) return alert("Bonus From must be before Bonus To.")
    setSaving(true)
    try {
      await api.put("update-center-bonus-bill/" + editData.id, v)
      fetchData(); setEditModal(false)
    } catch (e) { alert("Error: " + (e.response?.data?.data || e.message)) }
    setSaving(false)
  }

  const handleDelete = async () => {
    try { await api.delete("delete-center-bonus-bill/" + deleteId); fetchData() }
    catch (e) { alert("Delete failed.") }
  }

  const handleMultiDelete = async () => {
    const ids = Object.keys(checkedItems).filter(k => checkedItems[k])
    try { await api.post("delete-multiple-center-bonus-bills", { items: ids }); setCheckedItems({}); setParentChecked(false); fetchData() }
    catch (e) { alert("Bulk delete failed.") }
  }

  const handleCheckbox = id => {
    setCheckedItems(prev => { const n = { ...prev, [id]: !prev[id] }; setParentChecked(data.length > 0 && data.every(r => n[r.id])); return n })
  }
  const handleParentCheckbox = () => {
    const n = !parentChecked; setParentChecked(n); const items = {}; data.forEach(r => { items[r.id] = n }); setCheckedItems(items)
  }
  const selectedCount = Object.values(checkedItems).filter(Boolean).length

  const filtered = useMemo(() => {
    let d = data
    if (fPlant)    d = d.filter(b => (b.plantName||"").toLowerCase().includes(fPlant.toLowerCase()))
    if (fCC)       d = d.filter(b => (b.chillingCenterName||"").toLowerCase().includes(fCC.toLowerCase()))
    if (fRoute)    d = d.filter(b => (b.routeName||"").toLowerCase().includes(fRoute.toLowerCase()))
    if (fCenter)   d = d.filter(b => (b.collectionCenterName||"").toLowerCase().includes(fCenter.toLowerCase()))
    if (fDateFrom) d = d.filter(b => b.bonusBillDate >= fDateFrom)
    if (fDateTo)   d = d.filter(b => b.bonusBillDate <= fDateTo)
    if (fType)     d = d.filter(b => b.bonusType === fType)
    if (!search) return d
    const s = search.toLowerCase()
    return d.filter(b => Object.values(b).some(v => String(v || "").toLowerCase().includes(s)))
  }, [data, search, fPlant, fCC, fRoute, fCenter, fDateFrom, fDateTo, fType])

  const summaryCards = useMemo(() => {
    const total = filtered.reduce((s, b) => s + parseFloat(b.bonusAmount || 0), 0)
    return [
      { label: "Total Bills", value: filtered.length, icon: "fa-file-invoice", color: "#6366f1" },
      { label: "Total Bonus", value: R + total.toLocaleString("en-IN", { minimumFractionDigits: 2 }), icon: "fa-gift", color: "#22c55e" },
      { label: "Avg Bonus", value: filtered.length ? R + (total / filtered.length).toLocaleString("en-IN", { minimumFractionDigits: 2 }) : R + "0", icon: "fa-calculator", color: "#0ea5e9" },
      { label: "Selected", value: selectedCount, icon: "fa-check-square", color: "#f59e0b" },
    ]
  }, [filtered, selectedCount])

  const exportCSV = () => {
    if (!filtered.length) return
    const keys = ["plantName","chillingCenterName","routeName","collectionCenterName","bonusBillDate","bonusFrom","bonusTo","calculateOn","bonusValue","bonusAmount","bonusType","remarks"]
    const heads = ["Plant","Chilling Center","Route","Collection Center","Bill Date","Month From","Month To","Calculate On","Bonus Value","Bonus Amount","Bonus Type","Remarks"]
    const csv = [heads, ...filtered.map(r => keys.map(k => `"${String(r[k] || "").replace(/"/g, '""')}"`))]
      .map(r => r.join(",")).join("\n")
    const b = new Blob([csv], { type: "text/csv" })
    const l = document.createElement("a"); l.href = URL.createObjectURL(b)
    l.download = `bonus_bills_${new Date().toISOString().slice(0, 10)}.csv`; l.click()
  }

  const calcOnLabel = v => v === "1" ? "Qty Liters" : v === "2" ? "Qty KG" : v === "3" ? "Amount" : v || "-"

  const ib = (bg, icon, title, onClick, dis) => (
    <button title={title} onClick={onClick} disabled={dis}
      style={{ width: "32px", height: "32px", borderRadius: "4px", background: bg, color: "#fff", border: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "14px", cursor: dis ? "not-allowed" : "pointer", opacity: dis ? 0.5 : 1 }}>
      <i className={`fas ${icon}`}></i>
    </button>
  )


  // Form fields reusable for add/edit
  const renderForm = (d, plants, ccs, routes, colls, onPlantCh, onCCCh, onRouteCh, onCompCh, calcOn, onCalcOnCh) => {
    d = d || {}
    return (
      <>
        {/* Company (only if multi-company) */}
        {showCompany && (
          <Row>
            <Col md={6}><div className="mb-3">
              <label className="form-label fw-bold" style={{ fontSize: "13px" }}>Company</label>
              <select className="form-select" defaultValue={d.company || ""} onChange={e => onCompCh && onCompCh(e.target.value)} name="company">
                <option value="">All Companies</option>
                {companies.map((c, i) => <option key={i} value={c.id}>{c.name}</option>)}
              </select>
            </div></Col>
          </Row>
        )}

        {/* Row 1: Plant | Chilling Center */}
        <Row>
          <Col md={6}><div className="mb-3">
            <AvField type="select" name="plant" label="Plant *" required onChange={e => onPlantCh(e.target.value)} value={d.plant || ""}>
              <option value="">Select</option>
              {plants.map((p, i) => <option key={i} value={p.id}>{p.name}</option>)}
            </AvField>
          </div></Col>
          <Col md={6}><div className="mb-3">
            <AvField type="select" name="chillingCenter" label="Chilling Center *" required onChange={e => onCCCh(e.target.value)} value={d.chillingCenter || ""}>
              <option value="">Select</option>
              {ccs.map((c, i) => <option key={i} value={c.id}>{c.name}</option>)}
            </AvField>
          </div></Col>
        </Row>

        {/* Row 2: Route | Collection Center */}
        <Row>
          <Col md={6}><div className="mb-3">
            <AvField type="select" name="route" label="Route *" required onChange={e => onRouteCh(e.target.value)} value={d.route || ""}>
              <option value="">Select</option>
              {routes.map((r, i) => <option key={i} value={r.id}>{r.routeName}</option>)}
            </AvField>
          </div></Col>
          <Col md={6}><div className="mb-3">
            <AvField type="select" name="collectionCenter" label="Collection Center *" required value={d.collectionCenter || ""}>
              <option value="">Select</option>
              {colls.map((c, i) => <option key={i} value={c.id}>{c.centerName || c.collectionCenter}</option>)}
            </AvField>
          </div></Col>
        </Row>

        {/* Row 3: Bonus Bill Date | Bonus From */}
        <Row>
          <Col md={6}><div className="mb-3">
            <AvField name="bonusBillDate" label="Bonus Bill Date *" type="date" required value={d.bonusBillDate || today} />
          </div></Col>
          <Col md={6}><div className="mb-3">
            <AvField name="bonusFrom" label="Bonus From *" type="date" required value={d.bonusFrom || ""} />
          </div></Col>
        </Row>

        {/* Row 4: Bonus To | Calculate On */}
        <Row>
          <Col md={6}><div className="mb-3">
            <AvField name="bonusTo" label="Bonus To *" type="date" required value={d.bonusTo || ""} />
          </div></Col>
          <Col md={6}><div className="mb-3">
            <AvRadioGroup inline name="calculateOn" label="Calculate On *" required errorMessage="Select one" value={d.calculateOn || calcOn} onChange={e => onCalcOnCh && onCalcOnCh(e.target.value)}>
              <AvRadio label="Quantity Liters" value="1" />
              <AvRadio label="Quantity KG" value="2" />
              <AvRadio label="Amount" value="3" />
            </AvRadioGroup>
          </div></Col>
        </Row>

        {/* Row 5: Bonus Value | Bonus Amount */}
        <Row>
          <Col md={6}><div className="mb-3">
            <AvField name="bonusValue" label={bonusValueLabel(calcOn)} type="number" step="0.01" min="0" required value={d.bonusValue || ""} />
          </div></Col>
          <Col md={6}><div className="mb-3">
            <AvField name="bonusAmount" label="Bonus Amount" type="number" step="0.01" min="0" value={d.bonusAmount || "0"} />
          </div></Col>
        </Row>

        {/* Row 6: Bonus Type | Remarks */}
        <Row>
          <Col md={6}><div className="mb-3">
            <AvField type="select" name="bonusType" label="Bonus Type" value={d.bonusType || ""}>
              <option value="">Select Type</option>
              <option value="quality">Quality Bonus</option>
              <option value="quantity">Quantity Bonus</option>
              <option value="loyalty">Loyalty Bonus</option>
              <option value="festive">Festive Bonus</option>
              <option value="other">Other</option>
            </AvField>
          </div></Col>
          <Col md={6}><div className="mb-3">
            <AvField name="remarks" label="Remarks" type="textarea" rows="2" value={d.remarks || ""} />
          </div></Col>
        </Row>
      </>
    )
  }

  return (<>
    {/* ── Page header */}
    <div className="dashBody" style={{ marginTop: "10px" }}>
      <div className="font-size-18 mb-0 dashHeader" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <h4 style={{ margin: 0 }}>Center Bonus Bills</h4>
          {canAdd(MID) && ib("#28a745", "fa-plus", "Add New", () => { setAPlants(allPlants); setACCs([]); setARoutes([]); setAColls([]); setACalcOn("1"); setAddModal(true) }, false)}
          {canDelete(MID) && ib("#dc3545", "fa-trash", selectedCount > 0 ? `Delete ${selectedCount}` : "Delete Selected", () => { if (selectedCount > 0) setConfirmMulti(true); else alert("Select items first.") }, false)}
          {canExport(MID) && ib("#17a2b8", "fa-download", "Export CSV", exportCSV, !filtered.length)}
          <button title={showFilter ? "Hide Filters" : "Show Filters"} onClick={() => { setShowFilter(f => { if (f) clearFilters(); return !f }) }}
            style={{ width: "32px", height: "32px", borderRadius: "4px", background: showFilter ? "#0d6efd" : "#6c757d", color: "#fff", border: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "14px", cursor: "pointer" }}>
            <i className="fas fa-filter"></i>
          </button>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <input type="text" className="form-control form-control-sm" placeholder="Search..." style={{ width: "180px", borderRadius: "4px 0 0 4px" }} value={search} onChange={e => setSearch(e.target.value)} />
          <button className="btn btn-primary btn-sm" style={{ borderRadius: "0 4px 4px 0", height: "31px" }}><i className="fas fa-search"></i></button>
        </div>
      </div>

      {/* Summary cards */}
      <Row className="mb-3 mt-2">
        {summaryCards.map((c, i) => (
          <Col md={3} sm={6} key={i}>
            <div style={{ background: "#fff", borderRadius: "10px", padding: "14px 16px", borderLeft: `4px solid ${c.color}`, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
              <div style={{ width: "40px", height: "40px", borderRadius: "8px", background: c.color + "15", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className={`fas ${c.icon}`} style={{ color: c.color, fontSize: "16px" }}></i>
              </div>
              <div>
                <div style={{ fontSize: "20px", fontWeight: "700", color: "#1e293b" }}>{c.value}</div>
                <div style={{ fontSize: "11px", color: "#6c757d", textTransform: "uppercase", letterSpacing: "0.5px" }}>{c.label}</div>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {/* Table */}
      <div className="table-responsive">
        <Table className="table mb-0 dairyTbl">
          <thead>
            <tr>
              <th><input type="checkbox" checked={parentChecked} onChange={handleParentCheckbox} /></th>
              <th>#</th><th>Plant</th><th>Chilling Center</th><th>Route</th><th>Center</th>
              <th>Bonus Bill Date</th><th>Month From</th><th>Month To</th>
              <th>Bonus Amount</th><th>Action</th>
            </tr>
          </thead>
          <tbody>
            {showFilter && (
              <tr style={{ background: "#f0f4ff" }}>
                <td></td><td></td>
                <td><input type="text" className="form-control form-control-sm" placeholder="Plant..." value={fPlant} onChange={e => setFPlant(e.target.value)} /></td>
                <td><input type="text" className="form-control form-control-sm" placeholder="CC..." value={fCC} onChange={e => setFCC(e.target.value)} /></td>
                <td><input type="text" className="form-control form-control-sm" placeholder="Route..." value={fRoute} onChange={e => setFRoute(e.target.value)} /></td>
                <td><input type="text" className="form-control form-control-sm" placeholder="Center..." value={fCenter} onChange={e => setFCenter(e.target.value)} /></td>
                <td><input type="date" className="form-control form-control-sm" value={fDateFrom} onChange={e => setFDateFrom(e.target.value)} title="Bill Date From" /></td>
                <td><input type="date" className="form-control form-control-sm" value={fDateTo} onChange={e => setFDateTo(e.target.value)} title="Bill Date To" /></td>
                <td><select className="form-control form-control-sm" value={fType} onChange={e => setFType(e.target.value)}>
                  <option value="">All Types</option>
                  <option value="quality">Quality</option>
                  <option value="quantity">Quantity</option>
                  <option value="loyalty">Loyalty</option>
                  <option value="festive">Festive</option>
                  <option value="other">Other</option>
                </select></td>
                <td><button className="btn btn-sm btn-outline-danger" title="Clear filters" onClick={clearFilters}><i className="fa fa-times"></i></button></td>
                <td></td>
              </tr>
            )}
            {!dataLoaded
              ? <tr><td colSpan={11}><img className="loaderImg" src={loader} alt="Loading" /></td></tr>
              : filtered.length > 0
                ? filtered.map((b, i) => (
                    <tr key={b.id}>
                      <td><input type="checkbox" checked={!!checkedItems[b.id]} onChange={() => handleCheckbox(b.id)} /></td>
                      <td>{i + 1}</td>
                      <td>{b.plantName || "-"}</td>
                      <td>{b.chillingCenterName || b.chillingCenter || "-"}</td>
                      <td>{b.routeName || "-"}</td>
                      <td>{b.collectionCenterName || "-"}</td>
                      <td style={{ whiteSpace: "nowrap" }}>{b.bonusBillDate || "-"}</td>
                      <td style={{ whiteSpace: "nowrap" }}>{b.bonusFrom || "-"}</td>
                      <td style={{ whiteSpace: "nowrap" }}>{b.bonusTo || "-"}</td>
                      <td style={{ fontWeight: 700, color: "#059669" }}>{R}{parseFloat(b.bonusAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                      <td>
                        <div style={{ display: "flex", gap: "4px" }}>
                          {canEdit(MID) && <button title="Edit" onClick={() => openEdit(b.id)} style={{ width: "28px", height: "28px", borderRadius: "4px", background: "#0d6efd", color: "#fff", border: "none", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "12px" }}><i className="fas fa-edit"></i></button>}
                          {canDelete(MID) && <button title="Delete" onClick={() => { setDeleteId(b.id); setConfirmAlert(true) }} style={{ width: "28px", height: "28px", borderRadius: "4px", background: "#dc3545", color: "#fff", border: "none", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "12px" }}><i className="fas fa-trash"></i></button>}
                        </div>
                      </td>
                    </tr>
                  ))
                : <tr><td colSpan={11} className="text-center py-4"><i className="fas fa-inbox fa-2x text-muted mb-2 d-block"></i>No matching records found</td></tr>
            }
          </tbody>
        </Table>
      </div>
    </div>

    {/* ── Add Modal ── */}
    <Modal isOpen={addModal} toggle={() => setAddModal(false)} size="lg">
      <ModalHeader toggle={() => setAddModal(false)}>Center Bonus Bills <span style={{ color: "#dc3545", fontSize: "13px" }}>[* required]</span></ModalHeader>
      <ModalBody>
        <AvForm ref={addFormRef} onValidSubmit={handleAdd}>
          {renderForm(null, aPlants, aCCs, aRoutes, aColls, aPlantChange, aCCChange, aRouteChange, aCompanyChange, aCalcOn, setACalcOn)}
          <div className="text-end mt-2">
            <button type="submit" className="btn btn-success me-2" disabled={saving}>{saving ? <><Spinner size="sm" /> Saving...</> : "Generate"}</button>
            <button type="button" className="btn btn-danger" onClick={() => setAddModal(false)}>Close</button>
          </div>
        </AvForm>
      </ModalBody>
    </Modal>

    {/* ── Edit Modal ── */}
    <Modal isOpen={editModal} toggle={() => setEditModal(false)} size="lg">
      <ModalHeader toggle={() => setEditModal(false)}>Edit Center Bonus Bill <span style={{ color: "#dc3545", fontSize: "13px" }}>[* required]</span></ModalHeader>
      <ModalBody>
        {editData && (
          <AvForm ref={editFormRef} onValidSubmit={handleEdit} model={editData}>
            {renderForm(editData, ePlants, eCCs, eRoutes, eColls, ePlantChange, eCCChange, eRouteChange, null, eCalcOn, setECalcOn)}
            <div className="text-end mt-2">
              <button type="submit" className="btn btn-success me-2" disabled={saving}>{saving ? <><Spinner size="sm" /> Saving...</> : "Save Changes"}</button>
              <button type="button" className="btn btn-danger" onClick={() => setEditModal(false)}>Close</button>
            </div>
          </AvForm>
        )}
      </ModalBody>
    </Modal>

    {/* ── SweetAlerts ── */}
    {confirm_alert && <SweetAlert title="Are you sure?" warning showCancel confirmBtnBsStyle="success" cancelBtnBsStyle="danger"
      onConfirm={() => { handleDelete(); setConfirmAlert(false); setSuccessDlg(true); setDlgTitle("Deleted"); setDlgDesc("Record deleted.") }}
      onCancel={() => setConfirmAlert(false)}>You won&apos;t be able to revert this!</SweetAlert>}
    {confirm_multi && <SweetAlert title={`Delete ${selectedCount} items?`} warning showCancel confirmBtnBsStyle="success" cancelBtnBsStyle="danger"
      onConfirm={() => { handleMultiDelete(); setConfirmMulti(false); setSuccessDlg(true); setDlgTitle("Deleted"); setDlgDesc(`${selectedCount} record(s) deleted.`) }}
      onCancel={() => setConfirmMulti(false)}>You won&apos;t be able to revert this!</SweetAlert>}
    {success_dlg && <SweetAlert success title={dlg_title} onConfirm={() => setSuccessDlg(false)}>{dlg_desc}</SweetAlert>}
  </>)
}

export default VlccBonusBills
